/**
 * Khung ứng dụng — sidebar bên trái, gập thành ngăn kéo ở khổ điện thoại.
 *
 * **Nhóm theo CÂU HỎI người dùng đang hỏi, không theo nguồn dữ liệu.** Đây là bài học đo được của
 * Autopilot: bên đó từng nhóm theo nơi dữ liệu đến (*Nội dung / Phân tích / Hệ thống*), chạy được
 * ở **9 màn** và **hỏng ở 14** — *"Tài chính"* và *"Analytics"* cùng nằm dưới *Phân tích* trong
 * khi trả lời hai câu hoàn toàn khác nhau.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ĐỢT 15: dựng lại theo `Shopify-Colorizeyou/apps/web/src/components/Layout.tsx`.
 *
 * Ba thứ chép về, mỗi thứ sửa một khuyết điểm đo được của bản trước:
 *
 * 1. **Số bài chờ duyệt nằm trên thanh điện thoại.** Đó là câu trả lời cho *"có việc gì cho tôi
 *    không?"*, và nó phải sống sót khi Lynust đang ở bất kỳ màn nào khác. Dùng chung khoá cache
 *    với màn Hàng chờ nên **không tốn thêm một request nào** khi anh đang ở đó.
 * 2. **Tiêu đề nhóm là một dải nền có viền trên dưới**, không phải một dòng chữ trôi nổi. Ở 12
 *    mục, một dòng chữ nhỏ không đủ sức chia danh sách thành các khối đọc được.
 * 3. **Mục đang mở tô CAM nhạt + chữ cam**, không phải mảng navy đặc. Mảng đặc trong sidebar hút
 *    mắt mạnh hơn cả nội dung trang — mà sidebar chỉ là nơi để đi, không phải nơi để đọc.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";

import { api, xoaToken } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { HuyHieuLive } from "./giao-dien.tsx";
import { BtDong, BtMenu } from "./bieu-tuong.tsx";

interface MucNav {
  den: string;
  chu: string;
  cuoi?: boolean;
  /** Chưa dựng. Route vẫn gắn và hiện một trang nói rõ — **một mục dẫn tới hư vô còn tệ hơn**. */
  sapCo?: boolean;
}

/**
 * Nhóm theo câu hỏi, theo đúng thứ tự người dùng hỏi:
 *
 *   "hôm nay có việc gì?"     → (không nhóm) Tổng quan
 *   "viết cái gì?"            → Lên ý tưởng
 *   "đang chạy tới đâu?"      → Sản xuất
 *   "kết quả ra sao?"         → Kết quả
 *   "hệ thống nối thế nào?"   → Hệ thống
 */
const NHOM_NAV: { tieuDe: string | null; muc: MucNav[] }[] = [
  { tieuDe: null, muc: [{ den: "/", chu: "Tổng quan", cuoi: true }] },
  {
    tieuDe: "Lên ý tưởng",
    muc: [
      // Catalogue là nguồn ý tưởng của Luồng 2 — ba trong năm site lấy đề tài từ đây.
      { den: "/san-pham", chu: "Sản phẩm" },
      { den: "/chan-dung", chu: "Chân dung khách" },
      { den: "/nguon", chu: "Nguồn & báo cáo" },
    ],
  },
  {
    tieuDe: "Sản xuất",
    muc: [
      { den: "/hang-cho", chu: "Hàng chờ duyệt" },
      { den: "/lich", chu: "Lịch đăng" },
      { den: "/prompt", chu: "Cấu trúc prompt" },
    ],
  },
  {
    tieuDe: "Kết quả",
    muc: [
      { den: "/bai-hoc", chu: "Bài học" },
      { den: "/so-sanh-model", chu: "So sánh model" },
    ],
  },
  {
    tieuDe: "Hệ thống",
    muc: [
      { den: "/chi-phi", chu: "Chi phí AI" },
      { den: "/cai-dat", chu: "Cài đặt" },
      { den: "/ket-noi", chu: "Kết nối" },
    ],
  },
];

/** Chữ ký của app. Một chỗ duy nhất, nên hai nơi hiện nó không thể lệch nhau. */
function ChuKy() {
  return (
    <span className="flex min-w-0 items-baseline gap-1.5">
      <span className="text-base font-semibold tracking-tight text-muc-900">AutoIME</span>
      <span className="hidden text-2xs text-muc-500 sm:inline">nội dung cho Website IME</span>
    </span>
  );
}

function Nav({ dong, soChoDuyet }: { dong: () => void; soChoDuyet: number | undefined }) {
  return (
    <nav aria-label="Điều hướng chính" className="flex-1 overflow-y-auto pb-2">
      {NHOM_NAV.map((nhom, i) => (
        <div key={nhom.tieuDe ?? `nhom-${i}`} className={nhom.tieuDe ? "mb-1" : "px-3 pt-2"}>
          {nhom.tieuDe && (
            <h2 className="mb-1 border-y border-muc-200 bg-muc-100 px-5 py-1.5 text-2xs font-semibold uppercase tracking-[0.12em] text-muc-600">
              {nhom.tieuDe}
            </h2>
          )}
          <div className="space-y-0.5 px-3">
            {nhom.muc.map((m) => (
              <NavLink
                key={m.den}
                to={m.den}
                end={m.cuoi}
                onClick={dong}
                className={({ isActive }) =>
                  clsx(
                    "relative flex min-h-11 min-w-0 items-center justify-between gap-2 rounded-btn py-2 pl-3.5 pr-2.5 text-sm transition-colors lg:min-h-0",
                    isActive && !m.sapCo
                      ? "bg-canh-500/10 font-semibold text-canh-600"
                      : m.sapCo
                        ? "text-muc-400 hover:bg-muc-100"
                        : "text-muc-600 hover:bg-muc-100 hover:text-muc-900",
                  )
                }
              >
                <span className="min-w-0 truncate">{m.chu}</span>
                {m.sapCo ? (
                  <span className="shrink-0 text-2xs text-muc-400">chưa dựng</span>
                ) : (
                  m.den === "/hang-cho" &&
                  typeof soChoDuyet === "number" &&
                  soChoDuyet > 0 && (
                    // Nền cam, chữ gần đen. Đây là một trong hai chỗ cam xuất hiện trong khung —
                    // và nó mang đúng nghĩa của cam: có việc cần Lynust.
                    <span className="so-thang-cot shrink-0 rounded-full bg-canh-500 px-1.5 py-0.5 text-2xs font-semibold text-canh-fg">
                      {soChoDuyet}
                    </span>
                  )
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function Khung({ ten, cheDo }: { ten: string; cheDo: string | null }) {
  const { pathname } = useLocation();
  const [moNgan, datMoNgan] = useState(false);

  /**
   * Con số duy nhất đáng mang theo trên mọi màn: nó là câu trả lời cho *"có việc gì cho tôi
   * không?"*. Dùng chung khoá cache với màn Hàng chờ, nên khi Lynust đang ở đó thì nó **không tốn
   * thêm request nào**.
   */
  const soChoDuyet = useQuery({
    queryKey: khoa.hangCho,
    queryFn: api.hangCho,
    refetchInterval: 30_000,
    select: (ds) => ds.filter((b) => b.trangThai === "PENDING_REVIEW").length,
  }).data;

  // Đổi trang thì đóng ngăn kéo. Một ngăn kéo sống sót qua điều hướng nhốt người dùng sau chính
  // cái menu của mình.
  useEffect(() => datMoNgan(false), [pathname]);

  useEffect(() => {
    if (!moNgan) return;
    const nghe = (e: KeyboardEvent) => e.key === "Escape" && datMoNgan(false);
    window.addEventListener("keydown", nghe);
    return () => window.removeEventListener("keydown", nghe);
  }, [moNgan]);

  return (
    <div className="min-h-screen bg-muc-50 text-muc-900">
      {/* Thanh điện thoại. Mang số chờ duyệt theo, để câu trả lời cho "có việc gì cho tôi không?"
          sống sót khi đang ở bất kỳ màn nào khác. */}
      <header className="sticky top-0 z-30 border-b border-muc-300 bg-muc-0/90 pt-[env(safe-area-inset-top)] backdrop-blur lg:hidden">
        <div className="flex h-14 items-center gap-1 px-2">
          <button
            onClick={() => datMoNgan(true)}
            aria-label="Mở menu"
            aria-expanded={moNgan}
            className="flex h-11 w-11 items-center justify-center rounded-btn text-muc-600 hover:bg-muc-100 hover:text-muc-900"
          >
            <BtMenu className="h-5 w-5" />
          </button>
          <ChuKy />
          <div className="ml-auto flex items-center gap-2 pr-1">
            {typeof soChoDuyet === "number" && soChoDuyet > 0 && (
              <NavLink
                to="/hang-cho"
                className="flex h-11 items-center gap-1.5 rounded-btn px-2 text-sm font-medium text-canh-600 hover:bg-canh-500/10"
              >
                <span className="so-thang-cot">{soChoDuyet}</span>
                <span className="text-muc-600">chờ duyệt</span>
              </NavLink>
            )}
          </div>
        </div>
      </header>

      <div className="flex">
        {moNgan && (
          <div
            className="fixed inset-0 z-40 bg-muc-900/40 lg:hidden"
            onClick={() => datMoNgan(false)}
            aria-hidden="true"
          />
        )}

        <aside
          className={clsx(
            "shrink-0 flex-col border-r border-muc-300 bg-muc-0 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] lg:w-60 lg:pt-0",
            moNgan
              ? "animate-truot-vao fixed inset-y-0 left-0 z-50 flex w-64 overflow-y-auto"
              : "hidden lg:sticky lg:top-0 lg:flex lg:h-screen",
          )}
        >
          <div className="flex h-14 items-center justify-between gap-2 border-b border-muc-300 px-5 lg:h-auto lg:py-4">
            <ChuKy />
            <button
              onClick={() => datMoNgan(false)}
              aria-label="Đóng menu"
              className="-mr-2 flex h-10 w-10 items-center justify-center rounded-btn text-muc-600 hover:bg-muc-100 hover:text-muc-900 lg:hidden"
            >
              <BtDong className="h-5 w-5" />
            </button>
          </div>

          {cheDo !== null && (
            <div className="border-b border-muc-200 px-5 py-2.5">
              <HuyHieuLive live={cheDo === "live"} />
            </div>
          )}

          <Nav dong={() => datMoNgan(false)} soChoDuyet={soChoDuyet} />

          <div className="border-t border-muc-200 px-5 py-3">
            <p className="min-w-0 truncate text-xs text-muc-600" title={ten}>
              {ten}
            </p>
            <button
              onClick={() => {
                xoaToken();
                location.reload();
              }}
              className="mt-0.5 text-xs text-muc-500 underline hover:text-muc-900"
            >
              Thoát
            </button>
          </div>
        </aside>

        <main className="min-w-0 flex-1 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:p-6 lg:h-screen lg:overflow-auto lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
